<template>
  <FloatLabel :variant="variant">
    <DatePicker
      :model-value="modelValue"
      @update:modelValue="emit('update:modelValue', $event ? $event : null)"
      @clear-click="emit('update:modelValue', null), emit('clear')"
      @show="panelOpen = true; emit('show')"
      @hide="panelOpen = false; emit('hide')"
      :showTime="showTime"
      :hourFormat="hourFormat"
      :stepMinute="stepMinute"
      :minDate="minDate"
      :maxDate="maxDate"
      :selectionMode="selectionMode"
      :view="view"
      :disabled="disabled"
      :invalid="error"
      :placeholder="placeholder"
      :numberOfMonths="numberOfMonths"
      dateFormat="yy-mm-dd"
      :manualInput="false"
      :showButtonBar="showButtonBar"
      showIcon
      :showClear="showClear"
      :size="size"
      iconDisplay="input"
      :pt="{
        root: {
          class: [
            'w-full',
          ]
        },

        pcInputText: {
          root: ({ state, context }) => ({
            class: [
              minimal ? 'border-0 border-b rounded-none bg-transparent ring-0' : 'border',
              // Positioning
              'pl-10', // Moving the input over due to icon being now on the left
              'pr-4',

              // Size
              'w-full',

              // Color and Background
              'focus:border-iris-600',
              'border-charcoal-200 dark:border-charcoal-800',
              {'focus:ring-2 focus:ring-iris-300/40 dark:focus:ring-iris-400/40': !minimal},
              'placeholder:text-charcoal-700 dark:placeholder:text-charcoal-400',

              // Disabled State
              { 'cursor-not-allowed pointer-events-auto opacity-70': context.disabled },

              // Changed State
              { 'border-yellow-500 ring-2 ring-yellow-500/40': !state.focused && changed && !error },

              // Invalid State
              { 'border-red-500 ring-2 ring-red-500/40': !state.focused && error && !changed }
            ]
          }),
        },
        buttonBar: {
          class: [
            // Positioning
            'justify-between',
            'mt-2',
            'pt-3'
          ]
        },
        inputIconContainer: {
          class: [
            // Positioning
            'left-0', // Moving the icon to the left side
            'ml-3',
            'w-fit'
          ]
        },
        pcTodayButton: {
          root: {
            class: [
              // Display
              'hidden'
            ]
          }
        },
        clearIcon: {
          class: [
            'right-3'
          ]
        },
        panel: {
          class: [
            'border-charcoal-200 dark:border-charcoal-800'
          ]
        }
      }"
    >
      <!-- Clear Button Slot -->
       <template #clearbutton="{ actionCallback }">
         <Button color="purple" outline size="sm" @click="actionCallback">
           Clear
         </Button>
       </template>

      <!-- Button Bar slot -->
      <template #buttonbar>
        <slot name="buttonbar"></slot>
      </template>
    </DatePicker>

    <!-- Float Label label -->
    <label v-if="label" :class="computedLabelClass">{{ label }}</label>
  </FloatLabel>
</template>

<script setup>
import DatePicker from 'primevue/datepicker'
import FloatLabel from 'primevue/floatlabel'
import Button from '@/components/Core/Button.vue'
import { ref, computed } from 'vue'

const panelOpen = ref(false)

// Apply a left padding to keep the label text from overlapping with the
// calendar icon when within the datepicker's input box
const computedLabelClass = computed(() => {
  if (props.variant === 'in' || (!modelValue.value && !panelOpen.value)) {
    return 'pl-7'
  }
})

const props = defineProps({
  showTime: {
    type: Boolean
  },
  hourFormat: {
    type: String,
    default: '12'
  },
  stepMinute: {
    type: Number,
    default: 1
  },
  minDate: {
    type: Date
  },
  maxDate: {
    type: Date
  },
  numberOfMonths: {
    type: Number,
    default: 1
  },
  selectionMode: {
    type: String
  },
  placeholder: {
    type: String
  },
  view: {
    type: String
  },
  disabled: {
    type: Boolean
  },
  error: {
    type: Boolean
  },
  minimal: {
    type: Boolean
  },
  changed: {
    type: Boolean
  },
  showClear: {
    type: Boolean
  },
  // To show the cancel button to clear button
  showButtonBar: {
    type: Boolean
  },
  // Used in conjunction with variant prop for Float Label
  label: {
    type: String
  },
  // Used for the Float Label
  variant: {
    type: String,
    validator: (val) => ['in', 'on', 'over'].includes(val),
    default: null
  },
  // Controls the padding and text size of the input
  size: {
    type: String,
    validator: (val) => ['small', 'large'].includes(val),
    default: null
  },
})

const emit = defineEmits(['hide', 'show', 'update:modelValue', 'clear'])

const modelValue = defineModel()
</script>

<style lang="css">
  .dark .p-datepicker-day-selected-range{
    background: #514B69;
  }

  .p-datepicker-day-selected-range{
    background: #c6befe;
  }
</style>